import type { JSX } from 'react';
import type { Fill, Scene, SceneNode } from '@/core/render/scene';
import { qrPathData, topRoundedPath } from '@/core/render/svg';

/**
 * Предпросмотр сцены.
 *
 * Сцена превращается в настоящие React-элементы, без innerHTML и без
 * dangerouslySetInnerHTML: пользовательская строка физически не может стать
 * разметкой. Геометрия берётся из того же расчёта, что и экспорт.
 */

function fillValue(fill: Fill): string {
  return fill.kind === 'solid' ? fill.color : `url(#${fill.id})`;
}

function nodeElement(node: SceneNode, key: number): JSX.Element | null {
  switch (node.type) {
    case 'rect':
      return (
        <rect
          key={key}
          x={node.rect.x}
          y={node.rect.y}
          width={node.rect.w}
          height={node.rect.h}
          rx={node.radius || undefined}
          fill={fillValue(node.fill)}
        />
      );
    case 'topbar':
      return <path key={key} d={topRoundedPath(node.rect, node.radius)} fill={fillValue(node.fill)} />;
    case 'stroke':
      return (
        <rect
          key={key}
          x={node.rect.x}
          y={node.rect.y}
          width={node.rect.w}
          height={node.rect.h}
          rx={node.radius || undefined}
          fill="none"
          stroke={node.color}
          strokeWidth={node.width}
        />
      );
    case 'qr':
      return (
        <path
          key={key}
          d={qrPathData(node.matrix, node.moduleSize, node.rect.x, node.rect.y, node.quietZoneModules)}
          fill={node.dark}
          shapeRendering="crispEdges"
        />
      );
    case 'path':
      return <path key={key} d={node.d} fill={node.fill} />;
  }
}

function gradients(scene: Scene): JSX.Element | null {
  const found = new Map<string, { from: string; to: string }>();
  for (const node of scene.nodes) {
    if ((node.type === 'rect' || node.type === 'topbar') && node.fill.kind === 'gradient') {
      found.set(node.fill.id, { from: node.fill.from, to: node.fill.to });
    }
  }
  if (found.size === 0) return null;
  return (
    <defs>
      {[...found.entries()].map(([id, g]) => (
        <linearGradient key={id} id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={g.from} />
          <stop offset="1" stopColor={g.to} />
        </linearGradient>
      ))}
    </defs>
  );
}

export type ScenePreviewProps = {
  scene: Scene;
  className?: string;
};

export function ScenePreview({ scene, className }: ScenePreviewProps): JSX.Element {
  return (
    <svg
      className={className}
      viewBox={`0 0 ${scene.width} ${scene.height}`}
      width="100%"
      height="100%"
      role="img"
      aria-label={scene.meta.description}
      preserveAspectRatio="xMidYMid meet"
    >
      {gradients(scene)}
      {scene.nodes.map(nodeElement)}
    </svg>
  );
}
